/* eslint-disable */
import React from 'react';
import StatusPill from './StatusPill';

/**
 * DeploymentLocationShifts — shows shifts at a specific location.
 * Active shifts first, then planned, then recent closed.
 */
export default function DeploymentLocationShifts({ st, setSt, locationId, account, onSelectShift, onBack, onCreateShift }) {
  const loc = (st.locations || []).find(l => l.id === locationId);
  const shifts = (st.shifts || []).filter(s => s.locationId === locationId);
  const allocs = st.allocations || [];

  // Auto-activate planned shifts past their start time
  const effectiveStatus = (s) => {
    if (s.status === 'planned' && new Date(s.startAt) <= new Date()) return 'active';
    return s.status;
  };

  const sortOrder = { active: 0, planned: 1, closed: 2, closed_with_open_items: 2 };
  const sorted = [...shifts].sort((a, b) => {
    const sa = sortOrder[effectiveStatus(a)] ?? 3;
    const sb = sortOrder[effectiveStatus(b)] ?? 3;
    if (sa !== sb) return sa - sb;
    return new Date(b.startAt) - new Date(a.startAt);
  });

  const activeCount = sorted.filter(s => effectiveStatus(s) === 'active').length;
  const plannedCount = sorted.filter(s => effectiveStatus(s) === 'planned').length;

  const fmtDate = (d) => new Date(d).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
  const fmtTime = (d) => new Date(d).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      {/* Header */}
      <div style={{ padding: '8px 20px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--surface-app)', borderBottom: '1px solid var(--border-subtle)', flexShrink: 0 }}>
        <button onClick={onBack} style={{ width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: '1px solid var(--border-default)', borderRadius: '50%', color: 'var(--text-muted)', cursor: 'pointer' }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M15 18l-6-6 6-6"/></svg>
        </button>
        <div style={{ textAlign: 'center', flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-default)' }}>{loc?.name || 'Location'}</div>
          <div style={{ fontSize: 11, color: 'var(--text-subtle)', marginTop: 2 }}>{loc?.customer || ''}</div>
        </div>
        <button onClick={onCreateShift} style={{ minWidth: 36, height: 36, padding: '0 10px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: '1px solid var(--border-default)', borderRadius: 18, color: 'var(--text-muted)', cursor: 'pointer', fontSize: 12, fontWeight: 500 }}>+ New</button>
      </div>

      {/* Shift list */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
        {activeCount > 0 && (
          <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-subtle)', marginBottom: 10 }}>
            Active shifts <span style={{ marginLeft: 8, padding: '1px 6px', background: 'var(--surface-raised)', borderRadius: 999, fontSize: 10, color: 'var(--text-muted)' }}>{activeCount}</span>
          </div>
        )}

        {sorted.length === 0 && (
          <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--text-subtle)' }}>
            <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-muted)', marginBottom: 4 }}>No shifts yet</div>
            <div style={{ fontSize: 11 }}>Create a shift to start tracking device allocations.</div>
          </div>
        )}

        {sorted.map(shift => {
          const status = effectiveStatus(shift);
          const shiftAllocs = allocs.filter(a => a.shiftId === shift.id);
          const outCount = shiftAllocs.filter(a => a.status === 'out').length;
          const returnedCount = shiftAllocs.filter(a => a.status === 'returned').length;
          const totalCount = shiftAllocs.length;

          return (
            <div
              key={shift.id}
              onClick={() => onSelectShift(shift.id)}
              style={{
                background: 'var(--surface-default)', border: '1px solid var(--border-subtle)',
                borderRadius: 14, padding: 16, marginBottom: 10, cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-default)', marginBottom: 4 }}>
                    {shift.possessionRef} · {shift.title}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 6 }}>
                    Shift {shift.shiftNumber} · Week {shift.week} · {fmtDate(shift.startAt)}
                  </div>
                </div>
                <StatusPill status={status} />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
                <div style={{ fontSize: 11 }}>
                  <span style={{ color: 'var(--text-subtle)', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: 9, display: 'block', marginBottom: 2 }}>Window</span>
                  <span style={{ color: 'var(--text-default)', fontWeight: 500 }}>{fmtTime(shift.startAt)} → {fmtTime(shift.endAt)}</span>
                </div>
                <div style={{ fontSize: 11 }}>
                  <span style={{ color: 'var(--text-subtle)', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: 9, display: 'block', marginBottom: 2 }}>Supervisor</span>
                  <span style={{ color: 'var(--text-default)', fontWeight: 500 }}>{shift.supervisorUserId || '—'}</span>
                </div>
              </div>

              {totalCount > 0 && (
                <div style={{ display: 'flex', gap: 12, marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border-subtle)' }}>
                  <div style={{ flex: 1, fontSize: 11 }}>
                    <span style={{ display: 'block', fontSize: 16, fontWeight: 700, color: 'var(--text-default)', fontVariantNumeric: 'tabular-nums' }}>{outCount}</span>
                    <span style={{ color: 'var(--text-subtle)', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: 9 }}>Out</span>
                  </div>
                  <div style={{ flex: 1, fontSize: 11 }}>
                    <span style={{ display: 'block', fontSize: 16, fontWeight: 700, color: 'var(--text-default)', fontVariantNumeric: 'tabular-nums' }}>{returnedCount}</span>
                    <span style={{ color: 'var(--text-subtle)', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: 9 }}>Returned</span>
                  </div>
                  <div style={{ flex: 1, fontSize: 11 }}>
                    <span style={{ display: 'block', fontSize: 16, fontWeight: 700, color: 'var(--text-default)', fontVariantNumeric: 'tabular-nums' }}>{totalCount}</span>
                    <span style={{ color: 'var(--text-subtle)', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: 9 }}>Total</span>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
